import { Button, Form, Input, Switch } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type WorkflowStep, type WorkflowTemplate, workflowsApi } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { collectPdfDocs } from '@/form-renderer/pdfPreviewActions';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

type Props = {
  templateId: string;
  /** Full template — the default PDF list is derived from its PdfFileUpload entries. */
  workflow: WorkflowTemplate;
  /** The Welcome step being configured; null after a refresh removed it. */
  step: WorkflowStep | null;
  editable: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent refreshes the template. */
  onChanged: () => Promise<void>;
};

type FormValues = {
  titleEn: string;
  titleFr?: string;
  subheaderEn?: string;
  subheaderFr?: string;
  descriptionEn?: string;
  descriptionFr?: string;
  showLanguageSelector: boolean;
  hideEditInReview?: boolean;
  pdfListEn?: string;
  pdfListFr?: string;
};

/** One bullet per line; blank lines dropped. */
const parseLines = (raw: string | undefined): string[] =>
  (raw ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

/**
 * Welcome step config modal — title / subheader / description (EN + FR), the
 * language-selector toggle, and the free-text PDF bullet lists. First open on an
 * unconfigured step prefills the text fields from the i18n defaults and the PDF
 * lists from the workflow's live PDF entries (same source PdfGenerationStep
 * renders); after the first save the stored config is the single source.
 */
export function WelcomeConfigModal({
  templateId,
  workflow,
  step,
  editable,
  onClose,
  onChanged,
}: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const open = step !== null;
  // Which step id the form has already been seeded for this open session. The
  // parent re-resolves `step` (and passes a fresh `workflow`) on every template
  // refresh — including the 20s editor-lock poll — which changes these object
  // references without any real content change. Seeding once per open session
  // (keyed on the step id) stops that background refresh from wiping the
  // originator's unsaved title/description/etc. edits.
  const seededStepId = useRef<string | null>(null);

  useEffect(() => {
    if (!step) {
      seededStepId.current = null;
      form.resetFields();
      return;
    }
    if (seededStepId.current === step.id) return;
    seededStepId.current = step.id;
    const cfg = step.welcomeConfig;
    if (cfg) {
      form.setFieldsValue({
        titleEn: cfg.titleEn,
        titleFr: cfg.titleFr,
        subheaderEn: cfg.subheaderEn,
        subheaderFr: cfg.subheaderFr,
        descriptionEn: cfg.descriptionEn,
        descriptionFr: cfg.descriptionFr,
        showLanguageSelector: cfg.showLanguageSelector,
        hideEditInReview: cfg.hideEditInReview ?? false,
        pdfListEn: cfg.pdfListEn.join('\n'),
        pdfListFr: cfg.pdfListFr.join('\n'),
      });
    } else {
      // Never configured — seed from i18n defaults + the live PDF entry labels
      // so the originator starts from what the runtime would show anyway.
      form.setFieldsValue({
        titleEn: t('workflow.steps.types.Welcome.defaultName', { lng: 'en' }),
        titleFr: t('workflow.steps.types.Welcome.defaultName', { lng: 'fr' }),
        subheaderEn: t('formRenderer.welcome.pdfHeading', { lng: 'en' }),
        subheaderFr: t('formRenderer.welcome.pdfHeading', { lng: 'fr' }),
        descriptionEn: t('workflow.steps.types.Welcome.defaultDescription', { lng: 'en' }),
        descriptionFr: t('workflow.steps.types.Welcome.defaultDescription', { lng: 'fr' }),
        showLanguageSelector: true,
        hideEditInReview: false,
        pdfListEn: collectPdfDocs(workflow, 'en')
          .map((d) => d.label)
          .join('\n'),
        pdfListFr: collectPdfDocs(workflow, 'fr')
          .map((d) => d.label)
          .join('\n'),
      });
    }
  }, [step, form, t, workflow]);

  const handleSave = async () => {
    if (!step) return;
    const values = await form.validateFields();
    const titleEn = values.titleEn.trim();
    const titleFr = (values.titleFr ?? '').trim();
    const descriptionEn = (values.descriptionEn ?? '').trim();
    const descriptionFr = (values.descriptionFr ?? '').trim();
    setSaving(true);
    try {
      await workflowsApi.setWelcomeConfig(templateId, step.id, {
        titleEn,
        titleFr,
        subheaderEn: (values.subheaderEn ?? '').trim(),
        subheaderFr: (values.subheaderFr ?? '').trim(),
        descriptionEn,
        descriptionFr,
        showLanguageSelector: values.showLanguageSelector,
        hideEditInReview: !!values.hideEditInReview,
        pdfListEn: parseLines(values.pdfListEn),
        pdfListFr: parseLines(values.pdfListFr),
      });
      // Rename the STEP to match. The landing-card copy lives in
      // `welcomeConfig`, but the step's own title/description are what the
      // builder's step list and the runtime rail display — so without this the
      // originator edits the title here, gets a success toast, and the step
      // still reads "Get Started" everywhere. There is no other way to rename a
      // Welcome step: the Edit button is DynamicForm-only.
      //
      // Kept as a second call rather than folded into the welcome-config
      // endpoint so the domain's two concepts stay separate — a seed can still
      // set a short rail title and a long card title independently via the API.
      await workflowsApi.editStep(templateId, step.id, {
        titleEn,
        titleFr: titleFr || undefined,
        descriptionEn: descriptionEn || undefined,
        descriptionFr: descriptionFr || undefined,
      });
      message.success(t('workflow.steps.welcome.saved'));
      await onChanged();
      onClose();
    } catch (err) {
      handleApiError(err, t, t('workflow.steps.welcome.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <BmoModal
      open={open}
      onCancel={onClose}
      title={t('workflow.steps.welcome.modalTitle')}
      width={640}
      bodyStyle={{ maxHeight: '65vh', overflowY: 'auto' }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!editable}
          onClick={handleSave}
        >
          {t('common.save')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark={false} disabled={!editable}>
        <Form.Item
          name="titleEn"
          label={t('workflow.steps.welcome.titleEn')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t('workflow.steps.welcome.titleRequired'),
            },
          ]}
        >
          <Input maxLength={500} showCount />
        </Form.Item>
        <Form.Item name="titleFr" label={t('workflow.steps.welcome.titleFr')}>
          <Input maxLength={500} showCount />
        </Form.Item>
        <Form.Item name="descriptionEn" label={t('workflow.steps.welcome.descriptionEn')}>
          <Input.TextArea rows={3} maxLength={2000} showCount />
        </Form.Item>
        <Form.Item name="descriptionFr" label={t('workflow.steps.welcome.descriptionFr')}>
          <Input.TextArea rows={3} maxLength={2000} showCount />
        </Form.Item>
        <Form.Item name="subheaderEn" label={t('workflow.steps.welcome.subheaderEn')}>
          <Input maxLength={500} showCount />
        </Form.Item>
        <Form.Item name="subheaderFr" label={t('workflow.steps.welcome.subheaderFr')}>
          <Input maxLength={500} showCount />
        </Form.Item>
        <Form.Item
          name="pdfListEn"
          label={t('workflow.steps.welcome.pdfListEn')}
          extra={t('workflow.steps.welcome.pdfListHint')}
        >
          <Input.TextArea rows={5} />
        </Form.Item>
        <Form.Item
          name="pdfListFr"
          label={t('workflow.steps.welcome.pdfListFr')}
          extra={t('workflow.steps.welcome.pdfListHint')}
        >
          <Input.TextArea rows={5} />
        </Form.Item>
        <Form.Item
          name="showLanguageSelector"
          label={t('workflow.steps.welcome.showLanguageSelector')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="hideEditInReview"
          label={t('workflow.steps.welcome.hideEditInReview')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </BmoModal>
  );
}
